import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip, { type ChipProps } from '@mui/material/Chip';
import Radio from '@mui/material/Radio';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import type { ComponentProps, KeyboardEvent } from 'react';

import { CardGallery } from '../CardGallery/CardGallery';
import { CardIconList } from '../CardIconList/CardIconList';
import type { PhotoEvidenceItem } from '../PhotoEvidenceList/PhotoEvidenceList';
import { Typography } from '../Typography/Typography';

type CardChip = ChipProps & {
  isLoading?: boolean;
};

interface CardContentProps {
  images?: PhotoEvidenceItem[];
  title: React.ReactNode;
  action?: React.ReactNode;
  subtitle?: React.ReactNode;
  chip?: CardChip | CardChip[];
  children?: React.ReactNode;
}

interface CardProps extends CardContentProps, Omit<ComponentProps<typeof Box>, 'children' | 'onClick' | 'title'> {
  isSelected?: boolean;
  isFocused?: boolean;
  clickable?: boolean;
  muted?: boolean;
  selectable?: boolean;
  selectionVariant?: 'radio' | 'checkbox';
  onClick?: () => void;
  'data-testid'?: string;
}

const CardContent = ({ images, title, action, subtitle, chip, children }: CardContentProps) => {
  const cardTitle = title ? (
    <Typography variant="body1" component="span" data-testid="card-title" fontWeight="bold">
      {title}
    </Typography>
  ) : null;
  const chips = chip ? (Array.isArray(chip) ? chip : [chip]) : [];
  const chipElement = chips.length ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
      {chips.map((chipProps, index) => {
        const key = typeof chipProps.label === 'string' ? chipProps.label : index;
        return chipProps.isLoading ? (
          <Skeleton key={key} variant="rounded" width={30} />
        ) : chipProps.label ? (
          <Chip key={key} size="small" {...chipProps} />
        ) : null;
      })}
    </Stack>
  ) : null;
  const titleContent =
    cardTitle || (chipElement && !subtitle) ? (
      <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
        {cardTitle}
        {!subtitle ? chipElement : null}
      </Stack>
    ) : null;
  const actionContent = action ? (
    <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      {action}
    </div>
  ) : null;

  return (
    <div>
      {images ? (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <CardGallery images={images} />
        </Box>
      ) : null}
      {action ? (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {titleContent}
          {actionContent}
        </Stack>
      ) : (
        titleContent
      )}
      {subtitle ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {subtitle ? (
            <Typography variant="body2" color="secondary" data-testid="card-subtitle">
              {subtitle}
            </Typography>
          ) : null}

          {chipElement}
        </Box>
      ) : null}
      {children ? (
        <Box
          sx={{
            mt: titleContent ? 1 : 0,
          }}
        >
          {children}
        </Box>
      ) : null}
    </div>
  );
};

export const Card = ({
  images,
  title,
  action,
  subtitle,
  chip,
  isSelected = false,
  isFocused = false,
  children,
  clickable = false,
  muted = false,
  selectable = false,
  selectionVariant = 'radio',
  onClick,
  onKeyDown,
  role,
  sx,
  tabIndex,
  'data-testid': dataTestId,
  ...rest
}: CardProps) => {
  const hasInteractiveStyles = clickable || selectable || onClick != null;
  const isInteractive = onClick != null && !muted;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (!isInteractive || event.defaultPrevented || event.repeat) return;
    if (event.currentTarget !== event.target) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Box
      {...rest}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={isInteractive ? (role ?? 'button') : role}
      tabIndex={isInteractive ? (tabIndex ?? 0) : tabIndex}
      data-testid={dataTestId}
      sx={[
        (theme) => ({
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
          ...(hasInteractiveStyles && {
            ':hover': {
              cursor: 'pointer',
            },
            boxShadow: theme.shadows[2],
          }),
          ...(muted && {
            opacity: 0.4,
            pointerEvents: 'none',
          }),

          backgroundColor: isSelected ? theme.palette.primary[200] : theme.palette.background.paper,
          border: isSelected ? `1px solid ${theme.palette.primary[400]}` : `1px solid ${theme.palette.border.main}`,
          borderRadius: theme.spacing(1),
          outline: isFocused ? `2px solid ${theme.palette.primary.main}` : 'none',
          outlineOffset: '2px',
          padding: 2,
          transition: theme.transitions.create(['box-shadow', 'background-color', 'border-color']),
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {selectable ? (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {selectionVariant === 'checkbox' ? <Checkbox checked={isSelected} /> : <Radio checked={isSelected} />}
          <CardContent images={images} title={title} action={action} subtitle={subtitle} chip={chip}>
            {children}
          </CardContent>
        </Stack>
      ) : (
        <CardContent images={images} title={title} action={action} subtitle={subtitle} chip={chip}>
          {children}
        </CardContent>
      )}
    </Box>
  );
};

Card.displayName = 'Card';
Card.UnorderedList = CardIconList;
